import { useState, useEffect, memo, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Ruler, Users, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import Layout from '@/components/Layout';
import SEO from '@/components/SEO';
import { apiRequest, formatPrice, tagColors } from '@/lib/utils';

// Colorful bouncy castle fallback images - optimized
const CASTLE_FALLBACK_IMAGE = "https://images.unsplash.com/photo-1527529482837-4698179dc6ce?auto=format&fit=crop&w=400&q=50";

// Pagination settings
const CASTLES_PER_PAGE = 6;

// Optimized image URL helper - reduces image size for faster loading
const getOptimizedImageUrl = (url) => {
  if (!url) return CASTLE_FALLBACK_IMAGE;
  
  // If it's a base64 data URL, return as-is (already compressed by backend)
  if (url.startsWith('data:')) return url;
  
  // If it's an unsplash URL, add optimization params
  if (url.includes('unsplash.com')) {
    return url.includes('?') ? `${url}&w=400&q=50` : `${url}?auto=format&fit=crop&w=400&q=50`;
  }
  
  // For customer assets or other URLs
  return url;
};

// Memoized castle card for performance - simplified without heavy animations
const CastleCard = memo(({ castle, index }) => {
  const [imgError, setImgError] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  
  // Memoize the optimized image source
  const imgSrc = useMemo(() => {
    if (imgError) return CASTLE_FALLBACK_IMAGE;
    return getOptimizedImageUrl(castle.main_image);
  }, [castle.main_image, imgError]);
  
  // Only lazy load images below the fold (after first 3)
  const shouldLazyLoad = index >= 3;
  
  return (
    <Card 
      className="rounded-3xl overflow-hidden border-0 shadow-lg bg-white hover:-translate-y-1 transition-transform duration-200"
      data-testid={`castle-card-${castle.id}`}
    >
      {/* Image with skeleton loading */}
      <div className="relative h-48 md:h-56 bg-slate-200">
        {!imgLoaded && (
          <div className="absolute inset-0 bg-slate-200 animate-pulse" />
        )}
        <img 
          src={imgSrc}
          alt={castle.name}
          className={`w-full h-full object-cover transition-opacity duration-300 ${imgLoaded ? 'opacity-100' : 'opacity-0'}`}
          loading={shouldLazyLoad ? "lazy" : "eager"}
          decoding="async"
          fetchpriority={shouldLazyLoad ? "low" : "high"}
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgError(true)}
        />
        {castle.tags?.includes('Most Popular') && (
          <span className="absolute top-4 right-4 bg-red-500 text-white text-xs font-bold px-3 py-1.5 rounded-full shadow">
            Most Popular
          </span>
        )}
      </div>

      <CardContent className="p-5 md:p-6">
        {/* Name & Price */}
        <div className="flex items-start justify-between mb-3">
          <h3 className="font-outfit font-bold text-lg md:text-xl text-slate-900">{castle.name}</h3>
          <div className="text-right">
            <p className="font-outfit font-bold text-base md:text-lg text-sky-600">{formatPrice(castle.price)}</p>
          </div>
        </div>

        {/* Specs */}
        <div className="flex items-center gap-3 md:gap-4 text-sm text-slate-600 mb-4">
          <span className="flex items-center gap-1">
            <Ruler className="w-4 h-4" />
            {castle.length || 4}m x {castle.width || 4}m
          </span>
          <span className="flex items-center gap-1">
            <Users className="w-4 h-4" />
            {castle.age_range || '3-12 yrs'}
          </span>
        </div>

        {/* Tags */}
        {castle.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {castle.tags.slice(0, 3).map((tag, i) => (
              <span 
                key={i} 
                className={`text-xs font-semibold px-2 md:px-3 py-1 rounded-full ${tagColors[tag] || 'bg-slate-100 text-slate-600'}`}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <Link to={`/castles/${castle.id}`}>
          <Button className="w-full rounded-full bg-gradient-to-r from-sky-500 to-sky-600 hover:from-sky-600 hover:to-sky-700 text-white font-outfit font-bold text-sm md:text-base">
            View Details & Book
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => prevProps.castle.id === nextProps.castle.id && prevProps.index === nextProps.index);

export default function CastlesPage() {
  const [castles, setCastles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  // Initial load - fetch first page
  useEffect(() => {
    const fetchInitial = async () => {
      try {
        // Fetch count and first page in parallel
        const [countRes, castlesRes] = await Promise.all([
          apiRequest('/castles/count'),
          apiRequest(`/castles?page=1&limit=${CASTLES_PER_PAGE}`)
        ]);
        
        setTotalCount(countRes.count || 0);
        setCastles(castlesRes);
        setHasMore(castlesRes.length === CASTLES_PER_PAGE && castlesRes.length < countRes.count);
      } catch (err) {
        console.error('Failed to fetch castles:', err);
      } finally {
        setLoading(false);
      }
    };
    
    fetchInitial();
  }, []);

  // Load more function
  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    try {
      const nextPage = page + 1;
      const newCastles = await apiRequest(`/castles?page=${nextPage}&limit=${CASTLES_PER_PAGE}`);
      
      if (newCastles.length > 0) {
        setCastles(prev => [...prev, ...newCastles]);
        setPage(nextPage);
        setHasMore(newCastles.length === CASTLES_PER_PAGE && (castles.length + newCastles.length) < totalCount);
      } else {
        setHasMore(false);
      }
    } catch (err) {
      console.error('Failed to load more castles:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [page, loadingMore, hasMore, castles.length, totalCount]);

  return (
    <Layout>
      <SEO 
        title="Our Bouncy Castles"
        description="Browse our range of premium bouncy castles for hire in Auckland. Perfect for birthdays, school fairs, and community events. Safe, clean, and professionally delivered."
      />
      
      {/* Hero Banner with sky background */}
      <section className="relative py-12 md:py-16 overflow-hidden">
        {/* Sky gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#87CEEB] via-[#A8D8EA] to-[#C5E8F2]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="font-outfit font-extrabold text-4xl md:text-5xl text-slate-900 mb-4" data-testid="page-title">
            Our Bouncy Castles
          </h1>
          <p className="font-nunito text-lg text-slate-700 max-w-2xl mx-auto">
            Choose from our range of safe, clean, and exciting bouncy castles perfect for any occasion!
          </p>
        </div>
      </section>

      {/* Castles Grid with sky background */}
      <section className="py-12 md:py-16 relative overflow-hidden">
        {/* Sky gradient background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#C5E8F2] via-[#D8EEF5] to-[#E8F4F8]" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 z-10">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="rounded-3xl overflow-hidden border-0 shadow-lg bg-white">
                  <div className="h-56 bg-slate-200 animate-pulse" />
                  <CardContent className="p-6">
                    <div className="h-6 bg-slate-200 rounded animate-pulse mb-4" />
                    <div className="h-4 bg-slate-200 rounded animate-pulse mb-2 w-3/4" />
                    <div className="h-4 bg-slate-200 rounded animate-pulse mb-4 w-1/2" />
                    <div className="h-12 bg-slate-200 rounded-full animate-pulse" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : castles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600 font-nunito text-lg">No castles available at the moment. Check back soon!</p>
            </div>
          ) : (
            <>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {castles.map((castle, index) => (
                  <CastleCard key={castle.id} castle={castle} index={index} />
                ))}
              </div>
              
              {/* Load More Button */}
              {hasMore && (
                <div className="text-center mt-10">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    className="rounded-full bg-white text-slate-700 border-2 border-slate-300 hover:border-sky-500 hover:text-sky-600 font-outfit font-semibold px-8 py-3"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-slate-400 border-t-transparent rounded-full animate-spin" />
                        Loading...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <ChevronDown className="w-4 h-4" />
                        Load More Castles
                      </span>
                    )}
                  </Button>
                  <p className="text-slate-500 text-sm mt-2 font-nunito">
                    Showing {castles.length} of {totalCount} castles
                  </p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-outfit font-bold text-2xl md:text-3xl text-slate-900 mb-4">
            Not Sure Which Castle to Choose?
          </h2>
          <p className="text-slate-600 font-nunito mb-6">
            Contact us and we'll help you find the perfect bouncy castle for your event!
          </p>
          <Link to="/contact">
            <Button variant="outline" className="rounded-full border-2 border-slate-300 font-outfit font-semibold px-8 hover:border-sky-500 hover:text-sky-600">
              Contact Us
            </Button>
          </Link>
        </div>
      </section>
    </Layout>
  );
}
